import { computed, type ComputedRef } from 'vue'
import { useUserAccess, type UserAccess } from '@/composables/useUserAccess'
import { useSocietyStore } from '@/store/society'

export const FEATURES = {
  NEW_AP_VIEW: 'new_ap_view',
  PROVIDER_EVALUATION: 'provider_evaluation',
  PAYMENT_COMPLEMENTS: 'payment_complements',
  AI_REQUESTS: 'ai_requests',
  REQUEST_BREAKDOWN: 'request_breakdown',
  PAYMENT_COST_CENTER_SPLIT: 'payment_cost_center_split',
} as const

export type FeatureKey = typeof FEATURES[keyof typeof FEATURES]

type Rule = (access: UserAccess) => ComputedRef<boolean>

const RULES: Record<FeatureKey, Rule> = {
  // PAYMENT_COST_CENTER_SPLIT turns on one payment per cost center in the two manual payment forms,
  // behind the society setting in Configuración > Pagos (a setting, not a permission).
  [FEATURES.PAYMENT_COST_CENTER_SPLIT]: (_a) => {
    const societyStore = useSocietyStore()
    return computed(() => societyStore.splitManualPaymentsByCostCenter)
  },
  // REQUEST_BREAKDOWN gates breaking a purchase line down into child lines while the feature is
  // proven, behind the `HAS_REQUEST_BREAKDOWN` Permission. Only creating one is gated: a request
  // already broken down keeps showing and editing its rows, and the backend refuses new ones.
  [FEATURES.REQUEST_BREAKDOWN]: (_a) => {
    const societyStore = useSocietyStore()
    return computed(() => societyStore.hasPermission('HAS_REQUEST_BREAKDOWN'))
  },
  // AI_REQUESTS gates the chat that builds a request from free text, behind the
  // `HAS_AI_REQUESTS` Permission attached to the society. Per-client rollout: it cannot
  // be gated per policy, because the chat runs BEFORE a policy is chosen.
  [FEATURES.AI_REQUESTS]: (_a) => {
    const societyStore = useSocietyStore()
    return computed(() => societyStore.hasPermission('HAS_AI_REQUESTS'))
  },
  // NEW_AP_VIEW gates the reworked AP (CxP) invoices view behind the `HAS_NEW_AP_VIEW`
  // Permission attached to the society (per-society rollout, T21). Absent permission
  // => false => the old view renders.
  [FEATURES.NEW_AP_VIEW]: (_a) => {
    const societyStore = useSocietyStore()
    return computed(() => societyStore.hasPermission('HAS_NEW_AP_VIEW'))
  },
  // PROVIDER_EVALUATION gates the weighted provider-evaluation surfaces (config view,
  // directory column, provider history) behind `HAS_PROVIDER_EVALUATION`. It does NOT gate
  // rating a reception: that degrades on its own — a society with no configured criteria
  // keeps the single global star, so the reception form needs no flag.
  [FEATURES.PROVIDER_EVALUATION]: (_a) => {
    const societyStore = useSocietyStore()
    return computed(() => societyStore.hasPermission('HAS_PROVIDER_EVALUATION'))
  },
  // PAYMENT_COMPLEMENTS gates the CFDI-P withholding rule. The backend needs MEXICO as well, so
  // granting the permission to a society elsewhere turns nothing on — there is no country check
  // here because the endpoints already answer as if the rule were off.
  [FEATURES.PAYMENT_COMPLEMENTS]: (_a) => {
    const societyStore = useSocietyStore()
    return computed(() => societyStore.hasPermission('HAS_PAYMENT_COMPLEMENTS'))
  },
}

export function useFeatureFlag(feature: FeatureKey): ComputedRef<boolean> {
  return RULES[feature](useUserAccess())
}

// One-shot non-reactive read for router guards / imperative checks (outside setup scope).
// Use `useFeatureFlag` inside components/templates where reactivity is needed.
export function checkFeatureFlag(feature: FeatureKey): boolean {
  return RULES[feature](useUserAccess()).value
}
